import * as aesjs from 'aes-js';

const encryptionKey: string = process.env.REACT_APP_ENCRYPTION_KEY || '';
const encryptionIV: string = process.env.REACT_APP_ENCRYPTION_IV || '';

if (!encryptionKey || !encryptionIV) {
  console.error("WARNING: Encryption keys are missing! Check your .env file.");
}

export function AES_256_CBC_encrypt(message: string, key: string, iv: string): string | null {
  try {
    if (!key || !iv) {
      throw new Error("Missing encryption key or IV");
    }
    const keyBytes = stringToByteArray(key);
    let messageBytes = stringToByteArray(message);
    const ivBytes = stringToByteArray(iv);
    messageBytes = padBytes(messageBytes);
    const aesCipher = new aesjs.ModeOfOperation.cbc(keyBytes, ivBytes);
    const encryptedBytes = aesCipher.encrypt(messageBytes);
    const encryptedHex = bytesToHexString(encryptedBytes);
    return encryptedHex;
  } catch (error) {
    console.error("Encryption error:", error);
    return null;
  }
}

export function AES_256_CBC_decrypt(encryptedHex: string, key: string, iv: string): string | null {
  try {
    if (!key || !iv) {
      throw new Error("Missing encryption key or IV");
    }
    const keyBytes = stringToByteArray(key);
    const ivBytes = stringToByteArray(iv);
    const encryptedBytes = hexStringToBytes(encryptedHex);
    const aesCipher = new aesjs.ModeOfOperation.cbc(keyBytes, ivBytes);
    const decryptedBytes = aesCipher.decrypt(encryptedBytes);
    const decryptedMessage = unpadBytes(decryptedBytes);
    return byteArrayToString(decryptedMessage);
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
}

function padBytes(input: Uint8Array): Uint8Array {
  const blockSize = 16;
  const padding = blockSize - (input.length % blockSize);
  const paddedInput = new Uint8Array(input.length + padding);
  paddedInput.set(input);
  for (let i = input.length; i < paddedInput.length; i++) {
    paddedInput[i] = padding;
  }
  return paddedInput;
}

function unpadBytes(paddedInput: Uint8Array): Uint8Array {
  const padding = paddedInput[paddedInput.length - 1];
  return paddedInput.slice(0, paddedInput.length - padding);
}

function stringToByteArray(str: string): Uint8Array {
  const byteArray = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    byteArray[i] = str.charCodeAt(i);
  }
  return byteArray;
}

function byteArrayToString(byteArray: Uint8Array): string {
  let str = '';
  for (let i = 0; i < byteArray.length; i++) {
    str += String.fromCharCode(byteArray[i]);
  }
  return str;
}

function bytesToHexString(byteArray: Uint8Array): string {
  let hexString = '';
  for (let i = 0; i < byteArray.length; i++) {
    let hex = (byteArray[i] & 0xff).toString(16);
    hex = (hex.length === 1) ? '0' + hex : hex;
    hexString += hex;
  }
  return hexString;
}

function hexStringToBytes(hexString: string): Uint8Array {
  const byteArray: number[] = [];
  for (let i = 0; i < hexString.length; i += 2) {
    byteArray.push(parseInt(hexString.substr(i, 2), 16));
  }
  return new Uint8Array(byteArray);
}

/**
 * Decrypt API response data
 */
export function decryptResponseData(encryptedData: string): string | null {
  return AES_256_CBC_decrypt(encryptedData, encryptionKey, encryptionIV);
}

/**
 * Encrypt request payload
 */
export function encryptPayload(payload: string): string | null {
  return AES_256_CBC_encrypt(payload, encryptionKey, encryptionIV);
}
